import { lazy } from 'react';
import { Navigate, Outlet, createHashRouter, useParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useTwa } from '@/app/TwaProvider';
import { getReferralCode, setReferralCode } from '@/lib/referral';
import { referralCodeFromStartParam } from '@/lib/startParam';
import { useStartParamRoute } from '@/hooks/useStartParamRoute';
import { LoginNotificationPopup } from '@/components/LoginNotificationPopup';
import { useReturnProviderFunds } from '@/hooks/useReturnProviderFunds';
import { AutoSignInLoader, useTelegramAutoSignIn } from '@/hooks/useTelegramAutoSignIn';
import { WeakPasswordPopup } from '@/components/WeakPasswordPopup';

const Home = lazy(() => import('@/pages/Home'));
const Game = lazy(() => import('@/pages/Game'));
const GameList = lazy(() => import('@/pages/Game/List'));
const GameDetails = lazy(() => import('@/pages/Game/Details'));
const GameFavorites = lazy(() => import('@/pages/Game/Favorites'));
const GameRecommend = lazy(() => import('@/pages/Game/Recommend'));
const GamePlay = lazy(() => import('@/pages/Game/Play'));
const Deposit = lazy(() => import('@/pages/Wallet/Deposit'));
const Withdraw = lazy(() => import('@/pages/Wallet/Withdraw'));
const WalletRecord = lazy(() => import('@/pages/Wallet/Record'));
const WalletRecordDetail = lazy(() => import('@/pages/Wallet/RecordDetail'));
const WalletResult = lazy(() => import('@/pages/Wallet/Result'));
const Activity = lazy(() => import('@/pages/Activity'));
const ActivityDetails = lazy(() => import('@/pages/Activity/Details'));
const Mine = lazy(() => import('@/pages/Mine'));
const GameData = lazy(() => import('@/pages/Mine/GameData'));
const Referral = lazy(() => import('@/pages/Mine/Referral'));
const RebateRate = lazy(() => import('@/pages/Mine/RebateRate'));
const Vip = lazy(() => import('@/pages/Mine/Vip'));
const LanguagePage = lazy(() =>
  import('@/pages/Mine/SimplePages').then((m) => ({ default: m.LanguagePage })),
);
const Settings = lazy(() => import('@/pages/Mine/Settings'));
const MyPromotion = lazy(() => import('@/pages/Mine/Referral/MyPromotion'));
const CommissionDetails = lazy(() => import('@/pages/Mine/Referral/CommissionDetails'));
const TeamManagement = lazy(() => import('@/pages/Mine/Referral/TeamManagement'));
const CommissionSettings = lazy(() => import('@/pages/Mine/Referral/CommissionSettings'));
const CreateUser = lazy(() => import('@/pages/Mine/Referral/CreateUser'));
const TeamMembers = lazy(() => import('@/pages/Mine/Referral/TeamMembers'));
const TeamPerformance = lazy(() => import('@/pages/Mine/Referral/TeamPerformance'));
const ThirdPartyBets = lazy(() => import('@/pages/Mine/Referral/ThirdPartyBets'));
const AccountChangeList = lazy(() =>
  import('@/pages/Mine/Referral/AccountChange').then((m) => ({ default: m.AccountChangeList })),
);
const AccountChangeDetail = lazy(() =>
  import('@/pages/Mine/Referral/AccountChange').then((m) => ({ default: m.AccountChangeDetail })),
);
const InvitationLinkList = lazy(() =>
  import('@/pages/Mine/Referral/InvitationLink').then((m) => ({ default: m.InvitationLinkList })),
);
const InvitationLinkCreate = lazy(() =>
  import('@/pages/Mine/Referral/InvitationLink').then((m) => ({ default: m.InvitationLinkCreate })),
);
const InvitationLinkDetail = lazy(() =>
  import('@/pages/Mine/Referral/InvitationLink').then((m) => ({ default: m.InvitationLinkDetail })),
);
const BindAccount = lazy(() => import('@/pages/Mine/Settings/BindAccount'));
const ChangeLoginPassword = lazy(() => import('@/pages/Mine/Settings/ChangeLoginPassword'));
const PaymentPassword = lazy(() => import('@/pages/Mine/Settings/PaymentPassword'));
const SettingsAbout = lazy(() => import('@/pages/Mine/Settings/SettingsAbout'));
const SettingsPrivacy = lazy(() => import('@/pages/Mine/Settings/SettingsPrivacy'));
const SettingsTerms = lazy(() => import('@/pages/Mine/Settings/SettingsTerms'));
const WalletAddressList = lazy(() => import('@/pages/Mine/Settings/WalletAddressList'));
const WalletAddressAdd = lazy(() => import('@/pages/Mine/Settings/WalletAddressAdd'));
const WalletAddressEdit = lazy(() => import('@/pages/Mine/Settings/WalletAddressEdit'));
const AdvancedSecurity = lazy(() => import('@/pages/Mine/Settings/AdvancedSecurity'));
const BindGoogleAuthenticator = lazy(
  () => import('@/pages/Mine/Settings/BindGoogleAuthenticator'),
);
const LoginLanding = lazy(() => import('@/pages/Login/Landing'));
const SignIn = lazy(() => import('@/pages/Login/SignIn'));
const SignUp = lazy(() => import('@/pages/Login/SignUp'));
const TgQuick = lazy(() => import('@/pages/Login/TgQuick'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const NotificationDetails = lazy(() => import('@/pages/Notifications/Details'));
const Support = lazy(() => import('@/pages/Support'));

function RequireAuth() {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  if (!isAuthed) return <Navigate to="/login/signin" replace />;
  return <Outlet />;
}

function GuestOnly() {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  if (isAuthed) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Route-level transition removed — straight Outlet handoff. Slide
// transitions caused `position: fixed` widgets (WalletSwitch, etc.) to
// drag along with the page mid-animation; even a pure cross-fade was
// unnecessary visual noise on tab swaps. Pages render instantly.
function HomeRoute() {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  // startParam: populated by TwaProvider's useEffect once the TG SDK is ready
  // (native Telegram app). Falls back to localStorage for the web hash flow.
  const { startParam } = useTwa();
  // A `startapp` value naming a screen is not an invitation — useStartParamRoute
  // is taking the user there, and this must not divert them to sign-in first.
  const code = referralCodeFromStartParam(startParam) ?? getReferralCode();

  if (code && !isAuthed) {
    // Only redirect once per session so the user can close the login page and
    // browse the home page freely. The code stays in localStorage so the
    // signup form picks it up whenever the user decides to register.
    let prompted = false;
    try { prompted = sessionStorage.getItem('hg.referral-prompted') === '1'; } catch { /* ignore */ }
    if (!prompted) {
      try { sessionStorage.setItem('hg.referral-prompted', '1'); } catch { /* ignore */ }
      return <Navigate to="/login/signin" replace />;
    }
  }

  return <Home />;
}

function ReferralHome() {
  const { code } = useParams<{ code: string }>();
  if (code) setReferralCode(code);
  return <Home />;
}

function RouteHost() {
  // Opening the Mini App signs the visitor in on its own; this reports
  // whether that attempt is still running.
  const { pending: signingIn } = useTelegramAutoSignIn();
  // `t.me/<bot>?startapp=<screen>` lands on that screen, once the sign-in
  // above has settled.
  useStartParamRoute(signingIn);
  // Sweeps provider-wallet funds back on the navigations that call for it.
  useReturnProviderFunds();

  // Hold the whole tree back rather than just showing a spinner over it:
  // routes under RequireAuth would otherwise bounce the user to the sign-in
  // page a split second before the automatic sign-in lands.
  if (signingIn) return <AutoSignInLoader />;

  return (
    <>
      <Outlet />
      {/* Mounted once at the root: sign-in announcements must appear wherever
       *  the login flow lands the user, not only on Home. */}
      <LoginNotificationPopup />
      {/* Same reason, plus one more: it calls useNavigate/useLocation, so it
       *  must sit INSIDE the router. App.tsx wraps RouterProvider rather than
       *  living under it, and mounting it there threw on every render — a
       *  blank page, not a broken popup. */}
      <WeakPasswordPopup />
    </>
  );
}

export const router = createHashRouter([
  {
    element: <RouteHost />,
    children: [
      { path: '/', element: <HomeRoute /> },
      { path: '/r/:code', element: <ReferralHome /> },
      {
        element: <RequireAuth />,
        children: [
      { path: '/notifications', element: <Notifications /> },
      { path: '/notifications/:id', element: <NotificationDetails /> },
      { path: '/support', element: <Support /> },
      { path: '/game', element: <Game /> },
      { path: '/game/list/:tabId/:providerId?', element: <GameList /> },
      { path: '/game/details/:gameId', element: <GameDetails /> },
      { path: '/game/favorites', element: <GameFavorites /> },
      { path: '/game/recommend', element: <GameRecommend /> },
      { path: '/game/play/:gameId', element: <GamePlay /> },
      { path: '/deposit', element: <Deposit /> },
      { path: '/withdraw', element: <Withdraw /> },
      { path: '/wallet/record', element: <WalletRecord /> },
      { path: '/wallet/record/:id', element: <WalletRecordDetail /> },
      { path: '/wallet/result/:kind', element: <WalletResult /> },

      { path: '/activity', element: <Activity /> },
      { path: '/activity/:id', element: <ActivityDetails /> },

      { path: '/mine', element: <Mine /> },
      { path: '/mine/vip', element: <Vip /> },
      { path: '/mine/game-data', element: <GameData /> },
      { path: '/mine/rebate-rate', element: <RebateRate /> },
      { path: '/mine/language', element: <LanguagePage /> },
      { path: '/mine/settings', element: <Settings /> },
      { path: '/mine/settings/bind-account', element: <BindAccount /> },
      { path: '/mine/settings/change-password', element: <ChangeLoginPassword /> },
      { path: '/mine/settings/payment-password', element: <PaymentPassword /> },
      { path: '/mine/settings/about', element: <SettingsAbout /> },
      { path: '/mine/settings/privacy', element: <SettingsPrivacy /> },
      { path: '/mine/settings/terms', element: <SettingsTerms /> },
      { path: '/mine/settings/wallet-address', element: <WalletAddressList /> },
      { path: '/mine/settings/wallet-address/add', element: <WalletAddressAdd /> },
      { path: '/mine/settings/wallet-address/edit/:id', element: <WalletAddressEdit /> },
      { path: '/mine/settings/advanced-security', element: <AdvancedSecurity /> },
      { path: '/mine/settings/advanced-security/bind', element: <BindGoogleAuthenticator /> },
      { path: '/mine/referral', element: <Referral /> },
      { path: '/mine/referral/my-promotion', element: <MyPromotion /> },
      { path: '/mine/referral/commission', element: <CommissionDetails /> },
      { path: '/mine/referral/team', element: <TeamManagement /> },
      { path: '/mine/referral/team/create', element: <CreateUser /> },
      { path: '/mine/referral/team/:userId/commission', element: <CommissionSettings /> },
      { path: '/mine/referral/team-members', element: <TeamMembers /> },
      { path: '/mine/referral/team-performance', element: <TeamPerformance /> },
      { path: '/mine/referral/team-bets', element: <ThirdPartyBets /> },
      { path: '/mine/referral/team-account', element: <AccountChangeList /> },
      { path: '/mine/referral/team-account/:id', element: <AccountChangeDetail /> },
      { path: '/mine/referral/invitation', element: <InvitationLinkList /> },
      { path: '/mine/referral/invitation/create', element: <InvitationLinkCreate /> },
      { path: '/mine/referral/invitation/:id', element: <InvitationLinkDetail /> },
        ],
      },
      {
        element: <GuestOnly />,
        children: [
          { path: '/login', element: <LoginLanding /> },
          { path: '/login/signin', element: <SignIn /> },
          { path: '/login/signup', element: <SignUp /> },
          { path: '/login/signup/r/:code', element: <SignUp /> },
          { path: '/login/tg', element: <TgQuick /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
